// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import classNames from "classnames";
import { transformSecondsInHours, transformTimeToHoursAndMinutes } from "@transformers/date";
import { ComboBox, GSGrid } from "@components";
import type { StepProps, OptionType } from "@components";
import type { ShiftPatternDay } from "@modules/service/reducer/shift-patterns/types";
import { HoursMinutesDropdown } from "@modules/shared/components";
import type {
  NewShiftPattern,
  ShiftPattern,
  DraftShiftPattern,
} from "@modules/service/reducer/shift-patterns/types";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreator from "@modules/service/actionCreator";

type PropsFromState = $Exact<{
  draft: ?DraftShiftPattern,
  shiftPattern: ?ShiftPattern,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.UpdateShiftPatternDraft,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
  stepData: Object,
}>;

const active = null; // Dummy prop

export class Step2Pattern extends React.Component<Props> {
  get data() {
    return (this.props.draft || {}).shiftPatternDays || [];
  }

  get isWorkingDayOptions(): Array<{ value: boolean, label: string }> {
    return [
      { value: true, label: this.props.t("SHIFT_PATTERN_WORKING_DAY") },
      { value: false, label: this.props.t("SHIFT_PATTERN_NON_WORKING_DAY") },
    ];
  }

  get columns() {
    // TODO Grids: on row hover?
    return [
      {
        name: this.props.t("DAY"),
        key: "day",
        renderCell: ({ row }: { row: ShiftPatternDay }) => {
          return (
            <div className={classNames("sp__row sp__date")}>
              <div>{this.props.t(row.day)}</div>
            </div>
          );
        },
      },
      {
        name: this.props.t("SHIFT_PATTERN_DAY_TYPE"),
        key: "isWorkingDay",
        cellClass: "p-0",
        renderCell: ({ row, idx }: { row: ShiftPatternDay }) => (
          <ComboBox
            className="flex-fill"
            options={this.isWorkingDayOptions}
            value={{
              value: row.isWorkingDay,
              label: this.props.t(
                row.isWorkingDay ? "SHIFT_PATTERN_WORKING_DAY" : "SHIFT_PATTERN_NON_WORKING_DAY",
              ),
            }}
            onChange={this.handleUpdateIsWorkingDay(this.getItemIndex(row))}
            withoutEmptyOption
          />
        ),
      },
      {
        name: this.props.t("SHIFT_PATTERN_TIME_TO_WORK"),
        key: "shiftDurationSec",
        cellClass: "p-0",
        renderCell: ({ row }: { row: ShiftPatternDay }) => (
          <HoursMinutesDropdown
            id={row.day}
            seconds={row.shiftDurationSec}
            onChange={this.handleUpdateShiftDurationSec(this.getItemIndex(row))}
          />
        ),
      },
    ];
  }

  getItemIndex = (item: ShiftPatternDay) => this.data.indexOf(item);

  handleUpdateIsWorkingDay = (index: number) => (val: OptionType) => {
    const shiftPatternDays = this.data.map((day: ShiftPatternDay, i: number) =>
      i === index
        ? {
            ...day,
            isWorkingDay: val,
            shiftDurationSec: val ? day.shiftDurationSec : 0,
          }
        : day,
    );

    this.props.setDraft({
      ...this.props.draft,
      shiftPatternDays,
    });
  };

  handleUpdateShiftDurationSec = (index: number) => (seconds: number) => {
    const shiftPatternDays = this.data.map((day: ShiftPatternDay, i: number) =>
      i === index
        ? {
            ...day,
            shiftDurationSec: seconds,
          }
        : day,
    );

    this.props.setDraft({
      ...this.props.draft,
      shiftPatternDays,
    });
  };

  render() {
    if (!this.props.editable) {
      return null;
    }

    return (
      <React.Fragment>
        <div className="w-100 d-flex justify-content-center">
          <GSGrid columns={this.columns} rows={this.data} />
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state): PropsFromState => {
  return {
    draft: serviceSelectors.getShiftPatternDraft(state),
    shiftPattern: serviceSelectors.getCurrentShiftPattern(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.UpdateShiftPatternDraft,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(Step2Pattern);
